"use client"

import React, { useMemo, useState, useEffect } from "react"
import { XMasonry, XBlock } from "react-xmasonry"
import type {
  GameState,
  GameQuery,
  GameMarkets,
  Market,
} from "@azuro-org/toolkit"
import cx from "classnames"

import { Icon } from "components/ui"
import EmptyContent from "compositions/EmptyContent/EmptyContent"
import Tooltip from "components/feedback/Tooltip/Tooltip"

import OvertimeMarketTabs from "./components/OvertimeMarketTabs/OvertimeMarketTabs"

import useView from "./utils/useView"
import { useOvertimeCollapse } from "./utils/useCollapse"
import { MarketTypeGroupsBySport } from "@/Overtime/constants/marketTypes"
import type { Sport } from "overtime-utils"

// Using native JavaScript methods instead of lodash
import type { SportMarket } from "@/Overtime/types/markets"
import { isFuturesMarket, MarketType } from "overtime-utils"
import { getPositionTextV2, getTitleText } from "@/Overtime/utils/marketsV2"
import OvertimeOutcomeButton from "@/compositions/OutcomeButton/OvertimeOutcomeButton"
import { getGridMinMaxPercentage } from "@/helpers/getters/getGridMinMaxPercentage"
import { getMarketTooltip } from "@/Overtime/constants/marketTooltips"
import { useLive } from "@azuro-org/sdk"

export const MarketsSkeleton: React.FC = () => {
  return (
    <div>
      <div className="bone rounded-full w-full h-[2.875rem]" />
      {new Array(3).fill(0).map((_, index) => (
        <div key={index}>
          <div className="flex items-center justify-between p-4">
            <div className="bone h-[1.125rem] w-24 rounded-full" />
          </div>
          <div className="space-y-2 bg-accent-grey-850 rounded-sm p-2">
            <div className="flex justify-between">
              <div className="flex gap-2 w-full">
                <div className="bone h-10 w-full rounded-min" />
                <div className="bone h-10 w-full rounded-min" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

type ConditionProps = {
  condition: Market["conditions"][0]
  marketName: string
  game: NonNullable<GameQuery["game"]>
  betsSummary?: Record<string, string>
  isResult?: boolean
  isGameOpen: boolean
}

export const Condition: React.FC<any> = props => {
  const { isLive } = useLive()
  const { condition, marketName, isGameOpen, market } = props

  const customOrder = [0, 2, 1]

  const orderArray =
    marketName === "Winner" ? customOrder : condition.odds.map((_, i) => i)

  const oddsInfo = orderArray.map((i: number) => {
    const oddValue = condition.odds[i]
    const positionName = condition.positionNames?.[i] ?? ""

    // Handle both object and number cases for oddValue
    const finalOdd =
      typeof oddValue === "object" && oddValue !== null && "decimal" in oddValue
        ? oddValue.normalizedImplied
        : oddValue

    return {
      odd: finalOdd,
      position: i,
      positionName: positionName,
    }
  })

  const gridMinMaxPercentage = getGridMinMaxPercentage(condition, false)

  // Filter out invalid odds and check if we have any valid outcomes
  const validOddsInfo = oddsInfo.filter(
    outcome => outcome.odd !== undefined && outcome.odd !== 0
  )

  // Don't render the component if there are no valid outcomes
  if (validOddsInfo.length === 0) {
    return null
  }

  return (
    <div
      className={cx(
        "grid gap-2",
        marketName.toLowerCase().includes("sgp") || marketName === "SGP"
          ? "mb:grid-cols-1 ds:grid-cols-2"
          : ""
      )}
      style={{
        gridTemplateColumns:
          marketName.toLowerCase().includes("sgp") || marketName === "SGP"
            ? undefined
            : `repeat(auto-fit, minmax(calc(${gridMinMaxPercentage}% - 5px), 1fr))`,
      }}
    >
      {validOddsInfo.map((outcome, index) => {
        const key = outcome.position

        const positionText = getPositionTextV2(
          condition,
          outcome.position,
          true
        )
        const isZeroOdd =
          !outcome.odd ||
          outcome.odd == 0 ||
          condition.typeId === MarketType.EMPTY

        return (
          <OvertimeOutcomeButton
            key={key}
            marketName={marketName}
            outcome={outcome}
            positionText={positionText}
            position={outcome.position}
            playerProps={condition.playerProps}
            isZeroOdd={isZeroOdd}
            market={condition}
            size={28}
            isGameOpen={isGameOpen}
            fullMarket={market}
          />
        )
      })}
    </div>
    // </div>
  )
}

type ContentProps = {
  market: GameMarkets
  game: NonNullable<GameQuery["game"]>
  betsSummary?: Record<string, string>
  isResult?: boolean
  marketTypesFilter?: MarketType[]
}

const Content: React.FC<any> = props => {
  const { isLive } = useLive()
  const { market, groupedChildMarkets, marketTypesFilter = [] } = props
  const [activeMarketTab, setActiveMarketTab] = useState("")

  // Reset filter when sport or game changes
  useEffect(() => {
    setActiveMarketTab("")
  }, [market.sport, market.gameId])

  const { areAllCollapsed, collapsedMarketIds, collapse, collapseAll } =
    useOvertimeCollapse(groupedChildMarkets, market.typeId)

  const { activeView, changeView } = useView()

  const isGameStarted = market.maturityDate < new Date()
  const isGameOpen = isLive ? !market.isPaused : market.isOpen && !isGameStarted

  // Filter child markets based on selected tab
  const filteredGroupedChildMarkets = useMemo(() => {
    if (!activeMarketTab) {
      return groupedChildMarkets
    }

    // Get sport groups once outside the loop
    const sportGroups = MarketTypeGroupsBySport[market.sport as Sport] || {}

    // Convert activeMarketTab (which is a lowercase underscore format) back to the original group key
    const groupKey = activeMarketTab
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")

    // Get market type IDs for the selected group once
    // Find the correct key with case-insensitive matching
    const correctGroupKey =
      Object.keys(sportGroups).find(
        key => key.toLowerCase() === groupKey.toLowerCase()
      ) || groupKey
    const marketTypeIds = sportGroups[correctGroupKey] || []

    const filtered: Record<number, any[]> = {}
    Object.keys(groupedChildMarkets).forEach(key => {
      const typeId = Number(key)
      const childMarkets = groupedChildMarkets[typeId]

      if (marketTypeIds.includes(typeId)) {
        filtered[typeId] = childMarkets
      }
    })
    return filtered
  }, [groupedChildMarkets, activeMarketTab, market.sport])

  // Check if main market should be shown based on active tab
  const shouldShowMainMarket = useMemo(() => {
    if (!activeMarketTab) {
      return true // Show main market when no filter is selected
    }

    const sportGroups = MarketTypeGroupsBySport[market.sport as Sport] || {}
    const groupKey = activeMarketTab
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")

    const correctGroupKey =
      Object.keys(sportGroups).find(
        key => key.toLowerCase() === groupKey.toLowerCase()
      ) || groupKey

    const marketTypeIds = sportGroups[correctGroupKey] || []
    return marketTypeIds.includes(market.typeId)
  }, [activeMarketTab, market.sport, market.typeId])

  // Filter market types to only include those with valid odds
  const validMarketTypes = useMemo(() => {
    const validTypes: number[] = []

    // Check main market
    if (market.odds && market.odds.length > 0) {
      const hasValidMainOdds = market.odds.some((odd: any) => {
        const finalOdd =
          typeof odd === "object" && odd !== null && "decimal" in odd
            ? odd.normalizedImplied
            : odd
        return finalOdd !== undefined && finalOdd !== 0
      })
      if (hasValidMainOdds) {
        validTypes.push(market.typeId)
      }
    }

    // Check child markets
    Object.keys(groupedChildMarkets).forEach(key => {
      const typeId = Number(key)
      const childMarkets = groupedChildMarkets[typeId]

      const hasValidChildOdds = childMarkets.some(childMarket => {
        if (!childMarket.odds || childMarket.odds.length === 0) {
          return false
        }
        return childMarket.odds.some((odd: any) => {
          const finalOdd =
            typeof odd === "object" && odd !== null && "decimal" in odd
              ? odd.normalizedImplied
              : odd
          return finalOdd !== undefined && finalOdd !== 0
        })
      })

      if (hasValidChildOdds) {
        validTypes.push(typeId)
      }
    })

    return validTypes
  }, [market, groupedChildMarkets])

  return (
    <div className="bg-[#0F0E0D] -mx-2">
      {/* Market Filter Tabs */}

      <div className="px-4 py-3 border-b border-accent-grey-800">
        <OvertimeMarketTabs
          activeTab={activeMarketTab}
          onTabChange={setActiveMarketTab}
          sport={market.sport}
          availableMarketTypes={validMarketTypes}
        />
      </div>

      <div>
        <XMasonry maxColumns={10} targetBlockWidth={478}>
          {/* Only show main market if it matches the selected filter */}
          {shouldShowMainMarket && (
            <XBlock key="main-market" width={activeView === "columns" ? 1 : 2}>
              <MarketBlockItem
                typeId={market.typeId}
                market={market}
                onCollapse={collapse}
                childMarkets={[market]}
                collapsedMarketIds={collapsedMarketIds}
                isGameOpen={isGameOpen}
              />
            </XBlock>
          )}
          {Object.keys(filteredGroupedChildMarkets).map((key, index) => {
            const typeId = Number(key)
            const childMarkets = filteredGroupedChildMarkets[typeId]

            return (
              <XBlock
                key={`child-market-${key}`}
                width={activeView === "columns" ? 1 : 2}
              >
                <MarketBlockItem
                  typeId={Number(key)}
                  market={market}
                  onCollapse={collapse}
                  childMarkets={childMarkets}
                  collapsedMarketIds={collapsedMarketIds}
                  isGameOpen={isGameOpen}
                />
              </XBlock>
            )
          })}
        </XMasonry>
      </div>
    </div>
  )
}

interface MarketBlockItemProps {
  typeId: number
  market: Market
  onCollapse: (key: string) => void
  childMarkets: any[]
  collapsedMarketIds: string[]
  isGameOpen: boolean
}

export const MarketBlockItem: React.FC<MarketBlockItemProps> = ({
  typeId,
  childMarkets,
  collapsedMarketIds,
  onCollapse,
  market,
  isGameOpen,
}) => {
  const sortedChildMarkets = useMemo(
    () =>
      childMarkets.sort((a, b) => {
        if (a.line !== b.line) {
          return a.line - b.line
        }
        return b.odds - a.odds
      }),
    [childMarkets]
  )

  const name = useMemo(
    () => getTitleText(sortedChildMarkets[0]),
    [sortedChildMarkets]
  )

  // Check if any of the child markets have valid odds
  const hasValidOdds = useMemo(() => {
    return sortedChildMarkets.some(childMarket => {
      if (!childMarket.odds || childMarket.odds.length === 0) {
        return false
      }
      // Check if any odd is valid (not undefined, not 0)
      return childMarket.odds.some((odd: any) => {
        const finalOdd =
          typeof odd === "object" && odd !== null && "decimal" in odd
            ? odd.normalizedImplied
            : odd
        return finalOdd !== undefined && finalOdd !== 0
      })
    })
  }, [sortedChildMarkets])

  // Don't render the market block if it has no valid odds
  if (!hasValidOdds) {
    return null
  }

  const isCollapsed = collapsedMarketIds.includes(typeId.toString())

  return (
    <div className="px-2">
      <button
        className={cx(
          "flex items-center justify-between p-4 w-full group cursor-pointer bg-[#0F0E0D]",
          {
            "border-b border-b-accent-grey-1000": isCollapsed,
          }
        )}
        onClick={() => onCollapse(typeId.toString())}
      >
        <div className="flex items-center gap-2">
          <div className="text-caption-14 font-semibold">{name}</div>
          <Tooltip
            placement="top"
            mobPlacement="top"
            textSize="text-caption-12 h-fit"
            text={getMarketTooltip(name)}
          >
            <span className="inline-flex items-center cursor-pointer">
              <Icon
                name="interface/info-circle"
                className="size-4 text-accent-grey-400 hover:text-accent-grey-200 transition-colors"
              />
            </span>
          </Tooltip>
        </div>
        <div className="px-2 bg-accent-grey-1000 text-accent-grey-200 group-hover:bg-accent-grey-800 group-hover:text-accent-grey-30 rounded-ssm">
          <Icon
            className="size-4"
            name={
              isCollapsed ? "interface/chevron_down" : "interface/chevron_up"
            }
          />
        </div>
      </button>

      {!isCollapsed && (
        <div className="space-y-2 bg-[#1B1A17] rounded-sm p-2">
          {sortedChildMarkets.map((condition, index) => (
            <Condition
              key={`${condition.gameId}-${condition.typeId}-${condition.line}-${condition.playerProps.playerId}-${index}`}
              condition={condition}
              marketName={name}
              market={market}
              isGameOpen={isGameOpen}
            />
          ))}
        </div>
      )}
    </div>
  )
}

type MarketsProps = {
  market: SportMarket | any
  gameState: GameState
}

// const ResolvedMarkets: React.FC<MarketsProps> = ({ markets, gameState }) => {
//   const { address } = useAccount()
//   const { data: markets, isLoading } = useResolvedMarkets({
//     gameId: game.gameId,
//   })
//   const { data: betsSummary } = useBetsSummaryBySelection({
//     account: address!,
//     gameId: game.gameId,
//     gameState,
//   })

//   if (isLoading) {
//     return <MarketsSkeleton />
//   }

//   if (!markets?.length) {
//     return <div>Empty</div>
//   }

//   return (
//     <Content markets={markets} game={game} betsSummary={betsSummary} isResult />
//   )
// }
const WAIT_TIME = 600000

const ActiveMarkets: React.FC<MarketsProps> = ({ market, gameState }) => {
  const [hidePausedMarkets, setHidePausedMarkets] = useState(true)

  const groupedChildMarkets = useMemo(() => {
    const now = typeof window !== "undefined" ? new Date() : new Date(0)
    const filtered = market.childMarkets.filter(childMarket =>
      hidePausedMarkets && market.isOpen && market.maturityDate > now
        ? !childMarket.isPaused
        : true
    )
    return filtered.reduce(
      (acc, childMarket) => {
        const key = childMarket.typeId
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(childMarket)

        return acc
      },
      {} as Record<number, any[]>
    )
  }, [
    market.childMarkets,
    market.isOpen,
    market.maturityDate,
    hidePausedMarkets,
  ])

  // const numberOfMarkets = useMemo(() => {
  //   let num = 1 // Start with 1 for the main market
  //   Object.keys(groupedChildMarkets).forEach(key => {
  //     const typeId = Number(key)
  //     const childMarkets = groupedChildMarkets[typeId]
  //     num += childMarkets.length
  //   })
  //   return num
  // }, [groupedChildMarkets])

  const isGameStarted = useMemo(() => {
    const now = typeof window !== "undefined" ? new Date() : new Date(0)
    return market.maturityDate < now
  }, [market.maturityDate])
  const isGameOpen = market.isOpen && !isGameStarted
  const isGameResolved = market.isResolved || market.isCancelled
  // const isPendingResolution = isGameStarted && !isGameResolved
  // const showStatus = !market.isOpen || isGameStarted
  const liveScore = market.liveScore

  // const leagueSport = getLeagueSport(market.leagueId)

  // const isLiveScoreStatusHalfTime =
  //   liveScore &&
  //   (liveScore.gameStatus == GameStatus.RUNDOWN_HALF_TIME ||
  //     liveScore.gameStatus == GameStatus.OPTICODDS_HALF)

  // const {
  //   data: markets,
  //   isLoading,
  //   isPlaceholderData,
  // } = useActiveMarkets({
  //   gameId: game.id,
  //   query: {
  //     refetchInterval: 10_000,
  //   },
  // })

  // const isLive = gameState === GameState.Live

  // const startDate = +game.startsAt * 1000
  // const shouldWait = () => isLive && dayjs().diff(startDate) < WAIT_TIME
  // const [waitingTime, setWaitingTime] = useState(
  //   shouldWait() ? WAIT_TIME - dayjs().diff(startDate) : 0
  // )

  // useEffect(() => {
  //   if (shouldWait() && !markets?.length) {
  //     const interval = setInterval(() => {
  //       const newWaitingTime = Math.max(WAIT_TIME - dayjs().diff(startDate), 0)

  //       if (newWaitingTime === 0) {
  //         clearInterval(interval)
  //       }
  //       setWaitingTime(newWaitingTime)
  //     }, 1000)

  //     return () => {
  //       clearInterval(interval)
  //     }
  //   }
  // }, [gameState, markets])

  // if (isLoading || isPlaceholderData) {
  //   return <MarketsSkeleton />
  // }

  // if (!markets) {
  //   if (isLive) {
  //     if (waitingTime) {
  //       const time = dayjs.duration(waitingTime).format('mm:ss')

  //       return (
  //         <EmptyContent
  //           image="/images/illustrations/smile_sad.png"
  //           title={messages.livePending.title}
  //           text={{ ...messages.livePending.text, values: { time } }}
  //         />
  //       )
  //     } else {
  //       return (
  //         <EmptyContent
  //           image="/images/illustrations/smile_sad.png"
  //           title={messages.empty.live.title}
  //           text={messages.empty.live.text}
  //         />
  //       )
  //     }
  //   }

  //   return (
  //     <EmptyContent
  //       className="py-20"
  //       image="/images/illustrations/smile_sad.png"
  //       title={messages.empty.prematch.title}
  //       text={messages.empty.prematch.text}
  //     />
  //   )
  // }

  return <Content market={market} groupedChildMarkets={groupedChildMarkets} />
}

const OvertimeMarkets: React.FC<MarketsProps> = props => {
  const { gameState, market } = props

  const isGameStarted = useMemo(() => {
    const now = typeof window !== "undefined" ? new Date() : new Date(0)
    return market.maturityDate < now
  }, [market.maturityDate])
  const isGameResolved = market.isResolved || market.isCancelled
  const isPendingResolution = isGameStarted && !isGameResolved

  // TODO
  // if (market.isGameFinished) {
  //   return <div>ResolvedMarkets</div>
  //   // return <ResolvedMarkets {...props} />
  // }

  // if (isPendingResolution) {
  //   return (
  //     <EmptyContent
  //       className="py-20"
  //       image="/images/illustrations/smile_sad.png"
  //       title={messages.ended.title}
  //       text={messages.ended.text}
  //     />
  //   )
  // }

  return <ActiveMarkets {...props} />
}

export default OvertimeMarkets
